import * as React from "react";
import { useCallback, useEffect } from "react";
import { Control, useController, useWatch } from "react-hook-form";

import { useFlags } from "launchdarkly-react-client-sdk";

import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import Lens from "@mui/icons-material/Lens";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import type { SxProps } from "@mui/material/styles";

import CheckboxFormField from "components/checkbox-form-field";
import {
  APPOINTMENT_TYPES_CATEGORIES,
  APPOINTMENT_TYPE_SCHEDULE
} from "constants/appointment-types";
import { EVENT_TYPE_APPOINTMENT_BLOCK } from "constants/appointments";
import { APPOINTMENT_TYPE_DEFAULT_CHART_NOTE } from "constants/feature-flags";
import useReservedAppointmentTypes from "features/scheduling/scheduler/use-reserved-appointment-types.hook";
import {
  APPOINTMENT_TYPES_QUERY_WITH_DEFAULT_NOTES,
  useGetAppointmentTypesQuery
} from "services/appointment-types";
import { type AppointmentType } from "services/types/appointment-types.type";
import { formatDurationValue } from "utils/common";

import { AppointmentFormValues } from "../appointment-form.component";

type AppointmentTypeFieldProps = {
  control: Control<AppointmentFormValues, object>;
  disabled?: boolean;
  afterChange?: (data: AppointmentType | null) => void;
  sx?: SxProps;
};

export default function AppointmentTypeField({
  control,
  disabled = false,
  afterChange,
  sx
}: AppointmentTypeFieldProps) {
  const flags = useFlags();
  const isDefaultChartNoteEnabled = flags[APPOINTMENT_TYPE_DEFAULT_CHART_NOTE];

  const {
    field: { onChange, ...restField },
    formState: { errors }
  } = useController({
    name: "appointment_type",
    control,
    rules: {
      required: "Appointment Type is required"
    }
  });
  const { data: appointmentTypes, isLoading } = useGetAppointmentTypesQuery({
    is_active: true,
    is_clinic_schedule: true,
    exclude_type: APPOINTMENT_TYPE_SCHEDULE,
    ...(isDefaultChartNoteEnabled && {
      query: APPOINTMENT_TYPES_QUERY_WITH_DEFAULT_NOTES
    })
  });

  const [
    appointmentType,
    appointmentDate,
    startTime,
    endTime,
    eventType,
    staffMember
  ] = useWatch({
    control,
    name: [
      "appointment_type",
      "appointment_date",
      "start_time",
      "end_time",
      "event_type",
      "staff_member"
    ]
  });

  const { isReserved, setupAppointmentTypes } = useReservedAppointmentTypes();

  useEffect(() => {
    setupAppointmentTypes(
      appointmentDate,
      startTime,
      endTime,
      appointmentType?.id,
      staffMember?.id
    );
  }, [
    appointmentDate,
    startTime,
    endTime,
    setupAppointmentTypes,
    appointmentType?.id,
    staffMember?.id
  ]);

  const getRenderOption = useCallback(
    (
      props: any,
      { id, name, default_duration, color, general_type }: AppointmentType
    ) => {
      const duration = default_duration
        ? `(${formatDurationValue(default_duration?.slice(0, -3))})`
        : "";

      const generalType = general_type
        ? `(${APPOINTMENT_TYPES_CATEGORIES.getFromCode(general_type)})`
        : "";

      return (
        <ListItem {...props} key={id} alignItems="center">
          <Lens sx={{ color: color, mr: 1 }} />

          <ListItemText>
            {name} {duration} {generalType}
          </ListItemText>
        </ListItem>
      );
    },
    []
  );

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      sx={sx}
    >
      <MonitorHeartIcon sx={{ mt: 1.5, mr: 1.5 }} color="action" />
      <Stack direction="row" spacing={2} width="100%">
        <Box sx={{ minWidth: isReserved ? "290px" : "100%" }}>
          <Autocomplete
            disablePortal
            disabled={disabled}
            options={appointmentTypes || []}
            loading={isLoading}
            fullWidth
            popupIcon={<ExpandMoreOutlinedIcon />}
            renderOption={getRenderOption}
            getOptionLabel={({ name, default_duration }) =>
              `${name} ${
                default_duration
                  ? `(${formatDurationValue(default_duration?.slice(0, -3))})`
                  : ""
              }`
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            {...restField}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                {...(Boolean(errors?.appointment_type?.message)
                  ? {
                      error: true,
                      helperText: errors?.appointment_type?.message
                    }
                  : {})}
                label="Appointment Type"
              />
            )}
            onChange={(_, data) => {
              onChange(data);
              restField.onBlur();
              afterChange?.(data);
            }}
          />
        </Box>
        {Boolean(isReserved && eventType !== EVENT_TYPE_APPOINTMENT_BLOCK) && (
          <FormControlLabel
            label="Allow to create appointment within reserved blocks"
            control={
              <CheckboxFormField
                testId="overwrite_block"
                name="overwrite_block"
                control={control}
              />
            }
          />
        )}
      </Stack>
    </Box>
  );
}
